import React, { useCallback, useEffect, useState } from "react";
import { Calculator, Loader2, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/crm/LeadForm";

const LABEL = {
  cogs: "Cost of goods", freight: "Freight and transport", expenses: "Customer expenses",
  employee: "Employee cost", other: "Other direct cost",
};

export const ProfitTrail = ({ invoiceId, open, onOpenChange, onChanged }) => {
  const { can } = useAuth();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [value, setValue] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (silent) => {
    if (!invoiceId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/profit/invoice/${invoiceId}`);
      setRow(data);
    } catch (e) { toast.error(errMsg(e)); onOpenChange(false); } finally { setLoading(false); }
  }, [invoiceId]); // eslint-disable-line

  useEffect(() => { if (open) load(); }, [open, invoiceId]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/profit/invoice/${invoiceId}/override`, {
        field: edit, value: Number(value), reason,
      });
      setRow(data);
      setEdit(null);
      setReason("");
      toast.success("Correction saved with an audit entry");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const reset = async (field) => {
    try {
      const { data } = await api.delete(`/profit/invoice/${invoiceId}/override/${field}`);
      setRow(data);
      toast.success("Back to the calculated figure");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const editable = can("profitability", "edit");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">{row?.number || "Cost trail"}</SheetTitle>
        <SheetDescription className="sr-only">Full cost breakup and calculation trail for this invoice.</SheetDescription>

        {loading || !row ? (
          <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="bg-gradient-panel px-6 pb-5 pr-14 pt-8">
              <p className="num text-xs text-muted-foreground">{row.number} &middot; {fdate(row.date)}</p>
              <h2 className="truncate font-display text-2xl font-bold">{row.customer_name}</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Net revenue</p>
                  <p className="num text-lg font-bold" data-testid="pt-revenue">{money(row.revenue)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total direct cost</p>
                  <p className="num text-lg font-bold" data-testid="pt-cost">{money(row.total_cost)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Profit</p>
                  <p className={cn("num text-lg font-bold", row.profit < 0 ? "text-destructive" : "text-success")}
                    data-testid="pt-profit">{money(row.profit)} <span className="text-sm">({row.margin}%)</span></p>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Gross profit after goods and freight <b className="num">{money(row.gross_profit)}</b>
                {row.tax > 0 && <> &middot; GST of {money(row.tax)} is excluded from revenue</>}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <SheetClose asChild>
                  <Button size="sm" variant="ghost" data-testid="pt-close">Close</Button>
                </SheetClose>
              </div>
            </div>

            <div className="border-b border-border px-6 py-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cost breakup</p>
              <div className="space-y-1.5">
                {Object.keys(LABEL).map((f) => {
                  const over = (row.overridden || []).includes(f);
                  return (
                    <div key={f} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5 last:border-0"
                      data-testid={`pt-line-${f}`}>
                      <span className="text-sm">{LABEL[f]}
                        {over && (
                          <span className="ml-2 rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                            corrected from {money(row.base[f])}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="num text-sm font-semibold">{money(row[f])}</span>
                        {editable && (
                          <Button size="iconSm" variant="ghost" title="Correct this figure"
                            data-testid={`pt-edit-${f}`}
                            onClick={() => { setEdit(f); setValue(row[f]); setReason(""); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {editable && over && (
                          <Button size="iconSm" variant="ghost" title="Back to calculated"
                            data-testid={`pt-reset-${f}`} onClick={() => reset(f)}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <Tabs defaultValue="goods">
              <div className="scroll-slim overflow-x-auto border-b border-border px-6">
                <TabsList className="h-11 w-max bg-transparent p-0">
                  <TabsTrigger value="goods" data-testid="pt-tab-goods">Goods and freight</TabsTrigger>
                  <TabsTrigger value="expenses" data-testid="pt-tab-expenses">
                    Expenses{(row.trail.expenses || []).length ? ` (${row.trail.expenses.length})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="people" data-testid="pt-tab-people">People</TabsTrigger>
                  <TabsTrigger value="audit" data-testid="pt-tab-audit">
                    Audit{(row.history || []).length ? ` (${row.history.length})` : ""}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="goods" className="m-0 space-y-2 px-6 py-5">
                {(row.trail.cogs || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No stock was issued against this invoice.</p>
                )}
                {(row.trail.cogs || []).map((l, i) => (
                  <div key={i} className="rounded-lg border border-border px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{l.product}</p>
                      <p className="num text-sm font-semibold">{money(l.amount)}</p>
                    </div>
                    <p className="num text-xs text-muted-foreground">
                      {l.qty} x {money(l.unit_cost)} per unit
                      {l.freight_in_cost > 0 && <> &middot; includes freight {money(l.freight_in_cost)}</>}
                    </p>
                    <p className="text-xs italic text-muted-foreground">{l.basis}</p>
                    {l.freight_per_unit > 0 && (
                      <p className="num text-xs text-muted-foreground">
                        {money(l.base_cost)} purchase + {money(l.freight_per_unit)} freight per unit
                      </p>
                    )}
                    {(l.receipts || []).length > 0 && (
                      <details className="mt-1" data-testid="pt-receipts">
                        <summary className="cursor-pointer text-xs font-medium text-primary">
                          Show the receipts this cost came from
                        </summary>
                        <div className="mt-1 space-y-0.5">
                          {l.receipts.map((rc, k) => (
                            <p key={k} className="num text-xs text-muted-foreground">
                              {fdate(rc.date)} &middot; {rc.qty} x {money(rc.rate)}
                              {rc.freight_share > 0 && <> &middot; freight share {money(rc.freight_share)}</>}
                              {rc.reference && <> &middot; bill {rc.reference}</>}
                            </p>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="expenses" className="m-0 space-y-2 px-6 py-5">
                {(row.trail.expenses || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No customer expenses linked to this invoice or to this customer for the month.
                  </p>
                )}
                {(row.trail.expenses || []).map((e, i) => (
                  <div key={i} className="rounded-lg border border-border px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{e.category}</p>
                      <p className="num text-sm font-semibold">{money(e.charged)}</p>
                    </div>
                    <p className="num text-xs text-muted-foreground">
                      {e.number} &middot; {fdate(e.date)} &middot; full amount {money(e.full_amount)} &middot; from {e.source}
                    </p>
                    {e.note && <p className="text-xs text-muted-foreground">{e.note}</p>}
                    <p className="text-xs italic text-muted-foreground">{e.how}</p>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="people" className="m-0 space-y-2 px-6 py-5">
                {(row.trail.employee || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No employee cost - add monthly salaries in Staff Management to see this.
                  </p>
                )}
                {(row.trail.employee || []).map((e, i) => (
                  <div key={i} className="rounded-lg border border-border px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{e.who}</p>
                      <p className="num text-sm font-semibold">{money(e.charged)}</p>
                    </div>
                    <p className="text-xs italic text-muted-foreground">{e.how}</p>
                    {(e.items || []).map((x, j) => (
                      <p key={j} className="num text-xs text-muted-foreground">
                        {x.what} {x.ref} &middot; {x.days} day(s) x {money(x.day_rate)} = {money(x.cost)}
                      </p>
                    ))}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="audit" className="m-0 px-6 py-5">
                {(row.history || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nothing has been corrected by hand.</p>
                )}
                <div className="space-y-3">
                  {(row.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="relative pl-5">
                      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-warning" />
                      <p className="text-sm font-semibold">
                        {LABEL[h.field] || h.field}: {money(h.from)} &rarr;{" "}
                        {typeof h.to === "number" ? money(h.to) : h.to}
                      </p>
                      <p className="num text-xs text-muted-foreground">
                        calculated figure was {money(h.calculated)}
                      </p>
                      <p className="text-xs text-muted-foreground">{h.reason}</p>
                      <p className="text-[11px] text-muted-foreground">{h.by} &middot; {fdatetime(h.at)}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}

        <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Correct {LABEL[edit] || edit}</DialogTitle>
              <DialogDescription>
                The calculated figure is kept - your correction and the reason are recorded in the audit trail.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Field label="Corrected amount" required>
                <Input type="number" min="0" step="0.01" value={value} data-testid="pt-override-value"
                  onChange={(e) => setValue(e.target.value)} />
              </Field>
              <Field label="Why?" required>
                <Input value={reason} data-testid="pt-override-reason"
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Freight bill covers three invoices, this one carries a third" />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdit(null)} data-testid="pt-override-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="pt-override-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
};
